import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  CoachingReaction,
  CoachingReactionKind,
} from '@euler/model/coaching-practice';
import { memo, useEffect, useRef } from 'react';
import { Animated } from 'react-native';
import { aiColors, tint } from '../../../../../ai-suite/theme';

const kEmoji: Record<CoachingReactionKind, string> = {
  [CoachingReactionKind.Strong]: '👏',
  [CoachingReactionKind.Turnaround]: '✨',
  [CoachingReactionKind.Slip]: '🤔',
  [CoachingReactionKind.CriticalError]: '⚠️',
};

/** Background, then text. */
const kTones: Record<CoachingReactionKind, [string, string]> = {
  [CoachingReactionKind.Strong]: [aiColors.accent, aiColors.accentForeground],
  [CoachingReactionKind.Turnaround]: [
    aiColors.accent,
    aiColors.accentForeground,
  ],
  [CoachingReactionKind.Slip]: [tint(aiColors.amber, 0.12), aiColors.amber],
  [CoachingReactionKind.CriticalError]: [
    tint(aiColors.red, 0.1),
    aiColors.destructive,
  ],
};

/** The coach's reaction of the moment; it pops in and fades out. */
export const ReactionSlot = memo(
  ({ reaction }: { reaction: CoachingReaction | null }) => {
    const { rpx } = useResponsiveContext();
    const pop = useRef(new Animated.Value(0)).current;
    useEffect(() => {
      pop.setValue(0);
      if (reaction)
        Animated.spring(pop, {
          toValue: 1,
          friction: 5,
          useNativeDriver: true,
        }).start();
    }, [pop, reaction]);
    if (!reaction) return null;
    const [background, color] = kTones[reaction.kind];
    return (
      <Animated.View
        style={{
          alignSelf: 'flex-start',
          paddingHorizontal: rpx(10),
          paddingVertical: rpx(4),
          borderRadius: rpx(999),
          backgroundColor: background,
          opacity: pop,
          transform: [
            {
              scale: pop.interpolate({
                inputRange: [0, 1],
                outputRange: [0.8, 1],
              }),
            },
          ],
        }}
      >
        <Typography.Text size={rpx(11)} color={color} numberOfLines={1}>
          {`${kEmoji[reaction.kind]} ${reaction.text}`}
        </Typography.Text>
      </Animated.View>
    );
  },
);
